// useClock(period = 1000, enabled = true) → epoch ms, floored to the period
// One shared ticker aligned to the wall second drives every timer on the page (run clocks,
// "last event 0.8 s ago", "updated 2 s ago"). period 30_000 re-renders twice a minute (ago
// strings); enabled = false freezes the value (an ended run). The ticker stops when unused.
import { useRef, useSyncExternalStore } from "react";

let now = Date.now();
let timer: ReturnType<typeof setTimeout> | null = null;
const subscribers = new Set<() => void>();

function arm() {
  timer = setTimeout(tick, 1000 - (Date.now() % 1000) + 2);
}

function tick() {
  now = Date.now();
  subscribers.forEach((s) => s());
  arm();
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  if (!timer) {
    now = Date.now();
    arm();
  }
  return () => {
    subscribers.delete(cb);
    if (subscribers.size === 0 && timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
}

const noSubscribe = () => () => undefined;

function current(): number {
  // Nobody ticking: refresh a stale value (at most once per second, so repeated reads agree).
  if (!timer && Date.now() - now >= 1000) now = Date.now();
  return now;
}

export function useClock(period = 1000, enabled = true): number {
  const step = Math.max(1000, period);
  const frozen = useRef(0);
  const read = () => {
    if (!enabled && frozen.current) return frozen.current;
    const v = Math.floor(current() / step) * step;
    frozen.current = v;
    return v;
  };
  return useSyncExternalStore(enabled ? subscribe : noSubscribe, read, read);
}
